'use client';
import { Alert, MainTitle, semanticColor, TextButton } from '@repo/ui';
import { ReactNode } from 'react';

export const BrandResourceContent = () => {
  return (
    <div className={'flex flex-col pt-normal-xlarge pb-huge-medium'}>
      <Alert
        variant={'custom'}
        size={'medium'}
        className={'!py-normal-large !px-normal-medium !gap-normal-medium'}
        graphic={<img src={`${process.env.NEXT_PUBLIC_RESOURCE_CENTER_URL}/icons/icg_check-pencil_md.svg`} className={'w-10 h-10'} alt="check-pencil" />}
        bg={semanticColor.primary.background.lowest}
      >
        <div className={'flex flex-col gap-tiny-small'}>
          <h3 className={'body-medium-regular'}>
            비즈넵 로고는 서비스를 대표하는 상징 요소입니다. 다양한 매체에서 비즈넵 브랜드 아이덴티티를 올바르게 표현하고, 서비스의 이미지를 일관성 있게
            유지하기 위해 규정 범위 내에서 적용해주세요.
          </h3>
          <p className={'caption-small-regular text-neutral-foreground-low m-0'}>(Update 2025.05.21)</p>
        </div>
      </Alert>
      <BrandLogoContent />
      <ServiceLogoContent />
      <BrandColorContent />
      <BrandLogoUsageContent />
    </div>
  );
};

const BrandLogoContent = () => {
  return (
    <section id={'brand-logo'} className={'flex flex-col gap-normal-medium pt-spacious-large'}>
      <MainTitle
        size="medium"
        title={'브랜드 로고'}
        description={
          '한글 형태의 로고타입으로 가장 기본이 되는 로고입니다. 다양한 지면에서 일관된 커뮤니케이션을 위해 형태, 스타일 변형 없이 그대로 사용합니다. 로고의 시각적 독립성을 위해 상하좌우 최소 공간을 확보하여 사용해주세요.'
        }
      />
      <ImageCard imageFileName={'img_brand_bznav.png'} downloadFileName={'BZNAV_logo.zip'} />
      <AdditionalDescription>
        브랜드 로고 색상은 컬러 색상 사용을 원칙으로 하나, 적용매체의 상황을 고려하여 화이트·블랙 사용도 가능합니다.
      </AdditionalDescription>
      <div className={'flex gap-normal-medium compact:flex-col'}>
        <ImageCard imageFileName={'img_brand_bznav-black.png'} border />
        <ImageCard imageFileName={'img_brand_bznav-white.png'} />
      </div>
    </section>
  );
};

const ServiceLogoContent = () => {
  return (
    <section id={'service-logo'} className={'flex flex-col gap-normal-medium pt-spacious-large'}>
      <MainTitle size="medium" title={'서비스 로고'} description={'서비스 로고는 비즈넵과 서비스의 조합으로 이뤄지며, 브랜드 로고 사용 규정과 동일합니다.'} />
      <ImageCard topCaption={'비즈넵 환급'} imageFileName={'img_brand_bznav-refund.png'} downloadFileName={'BZNAV-Refund_logo.zip'} />
      <ImageCard topCaption={'비즈넵 케어'} imageFileName={'img_brand_bznav-care.png'} downloadFileName={'BZNAV-Care_logo.zip'} />
      <ImageCard topCaption={'비즈넵 세나'} imageFileName={'img_brand_bznav-sena.png'} downloadFileName={'BZNAV-Sena_logo.zip'} />
    </section>
  );
};

const BrandColorContent = () => {
  return (
    <section id={'brand-color'} className={'flex flex-col gap-normal-medium pt-spacious-large'}>
      <MainTitle size="medium" title={'브랜드 컬러'} description={'각 활용 매체에서 브랜드 및 서비스를 홍보할 시, 우선적 사용을 권장합니다.'} />
      <div className={'w-full h-[222px] rounded-bzc-medium compact:p-normal-medium p-normal-large bg-[#5A2EEA] flex flex-col gap-tiny-large text-white'}>
        <h4 className="heading-small-bold">BZNAV Violet</h4>
        <p className="body-medium-regular m-0">#5A2EEA</p>
        <p className="body-medium-regular m-0">R90 G46 B234</p>
      </div>
    </section>
  );
};

const BrandLogoUsageContent = () => {
  return (
    <section id={'brand-logo-usage'} className={'flex flex-col gap-normal-medium pt-spacious-large'}>
      <MainTitle
        size="medium"
        title={'로고 사용 유의사항'}
        description={'원활한 브랜드 커뮤니케이션을 위해, 브랜드 로고가 손상되지 않도록 아래의 예시를 참고하여 표준 형태로 사용해주세요.'}
      />
      <div className={'flex gap-normal-medium compact:flex-col'}>
        <ImageCard
          topCaption={
            <div className={'flex items-center'}>
              <img src={`${process.env.NEXT_PUBLIC_RESOURCE_CENTER_URL}/icons/icg_x_md.svg`} className={'w-7 h-7'} alt={'Do not change logo case 01'} />
              다른 로고타입으로 기재하지 마세요.
            </div>
          }
          imageFileName={'img_do-not-case_01.png'}
        />
        <ImageCard
          topCaption={
            <div className={'flex items-center'}>
              <img src={`${process.env.NEXT_PUBLIC_RESOURCE_CENTER_URL}/icons/icg_x_md.svg`} className={'w-7 h-7'} alt={'Do not change logo case 02'} />
              비율, 각도를 임의로 조정하지 마세요.
            </div>
          }
          imageFileName={'img_do-not-case_02.png'}
        />
      </div>
      <div className={'flex gap-normal-medium compact:flex-col'}>
        <ImageCard
          topCaption={
            <div className={'flex items-center'}>
              <img src={`${process.env.NEXT_PUBLIC_RESOURCE_CENTER_URL}/icons/icg_x_md.svg`} className={'w-7 h-7'} alt={'Do not change logo case 03'} />
              로고를 라인으로 변경하지 마세요.
            </div>
          }
          imageFileName={'img_do-not-case_03.png'}
        />
        <ImageCard
          topCaption={
            <div className={'flex items-center'}>
              <img src={`${process.env.NEXT_PUBLIC_RESOURCE_CENTER_URL}/icons/icg_x_md.svg`} className={'w-7 h-7'} alt={'Do not change logo case 04'} />
              컬러를 임의로 변경하지 마세요.
            </div>
          }
          imageFileName={'img_do-not-case_04.png'}
        />
      </div>
      <div className={'flex gap-normal-medium compact:flex-col'}>
        <ImageCard
          topCaption={
            <div className={'flex items-center'}>
              <img src={`${process.env.NEXT_PUBLIC_RESOURCE_CENTER_URL}/icons/icg_x_md.svg`} className={'w-7 h-7'} alt={'Do not change logo case 05'} />
              로고에 외곽선, 그림자 효과를 적용하지 마세요.
            </div>
          }
          imageFileName={'img_do-not-case_05.png'}
        />
        <ImageCard
          topCaption={
            <div className={'flex items-center'}>
              <img src={`${process.env.NEXT_PUBLIC_RESOURCE_CENTER_URL}/icons/icg_x_md.svg`} className={'w-7 h-7'} alt={'Do not change logo case 06'} />
              식별이 어려운 이미지와의 배치는 지양해주세요.
            </div>
          }
          imageFileName={'img_do-not-case_06.png'}
        />
      </div>
    </section>
  );
};

const AdditionalDescription = ({ children }: { children: ReactNode }) => {
  return <div className={'body-medium-regular text-neutral-foreground-low pt-normal-xlarge'}>{children}</div>;
};

const ImageCard = ({
  topCaption,
  imageFileName,
  downloadFileName,
  border = false
}: {
  topCaption?: ReactNode;
  imageFileName: string;
  downloadFileName?: string;
  border?: boolean;
}) => {
  return (
    <div
      className={`flex flex-col justify-between w-full rounded-bzc-medium bg-neutral-background-lowest aspect-[2.08] compact:p-normal-medium p-normal-large relative overflow-clip ${border ? 'border border-neutral-border-main' : ''}`}
    >
      <div className={'text-neutral-foreground-lowest body-small-medium'}>{topCaption ?? ''}</div>
      <img
        src={`${process.env.NEXT_PUBLIC_RESOURCE_CENTER_URL}/brand/resource/${imageFileName}`}
        className={'absolute top-0 left-0 w-full h-full object-cover'}
        alt={imageFileName}
      />
      {downloadFileName && (
        <div className={'flex justify-end w-full z-10'}>
          <a href={`${process.env.NEXT_PUBLIC_RESOURCE_CENTER_URL}/brand/resource/${downloadFileName}`} target="_blank" rel="noopener noreferrer">
            <TextButton size={'small'} variant={'primary'}>
               &nbsp;다운로드
            </TextButton>
          </a>
        </div>
      )}
    </div>
  );
};
